import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MaterialModule } from '../../material.module';
import { FormConfigService } from '../forms-config.service';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AppStateService } from '../../app-state.service';
import { MatGridListModule } from '@angular/material/grid-list';

export interface Form {
  created_at: string;        // Date string in ISO format
  version: number;           // Form version number
  type: string;              // Type of the form, e.g., 'consent'
  modified_by: string;       // User who last modified the form
  form_id: string;           // Unique identifier for the form
  created_by: string;        // User who created the form
  form_title: string;        // Title of the form
  modified_at: string;       // Date string in ISO format when form was last modified
}


@Component({
  selector: 'app-form-selector',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MaterialModule,
    MatGridListModule,
  ],
  templateUrl: './form-selector.component.html',
  styleUrls: ['./form-selector.component.scss']
})
export class FormSelectorComponent implements OnInit {
  patientId: string = '';
  clientId: string = '';
  @Input() searchQuery: string = '';
  formTemplates: Form[] = []; // Use the Form interface
  filteredFormTemplates: Form[] = []; // Use the Form interface
  selectedForm: Form | null = null; // Track selected form with Form interface
  appStateService = inject(AppStateService);
  formConfigService = inject(FormConfigService);
  snackBar = inject(MatSnackBar);
  loading = false;

  categorizedForms = {
    registration: [] as Form[], // Specify the type as an array of Form objects
    consent: [] as Form[], // Specify the type as an array of Form objects
    other: [] as Form[] // Specify the type as an array of Form objects
  };

  constructor(private router: Router) {
    this.clientId = this.appStateService.getState().clientId || '';
    this.patientId = this.appStateService.getState().patientId || '';
  }

  ngOnInit(): void {
    this.fetchFormTemplates();
  }

  fetchFormTemplates(): void {
    this.loading = true;
    this.formConfigService.getAllFormTemplates(this.clientId).subscribe({
      next: (templates: Form[]) => { // Expect an array of Form objects
        this.formTemplates = templates;
        this.filteredFormTemplates = templates;
        this.categorizeForms();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.showError("Error fetching form templates.");
      }
    });
  }

  categorizeForms(): void {
    this.categorizedForms = {
      registration: [],
      consent: [],
      other: []
    };

    this.formTemplates.forEach(template => {
      switch (template.type) {
        case 'registration':
          this.categorizedForms.registration.push(template);
          break;
        case 'consent':
          this.categorizedForms.consent.push(template);
          break;
        default:
          this.categorizedForms.other.push(template);
          break;
      }
    });
  }

  filterTemplates(query: string): void {
    const lowerCaseQuery = query.toLowerCase();
    this.filteredFormTemplates = this.formTemplates.filter(template =>
      template.form_title.toLowerCase().includes(lowerCaseQuery)
    );
    this.categorizeForms();
  }

  navigateToForm(template: Form): void { // Use the Form interface
    this.selectedForm = template; // Highlight selected form
    const route = this.getRouteForTemplate(template);
    if (route) {
      this.router.navigate(['/dashboard', route, template.form_id]);
    } else {
      this.showError('Form type not recognized.');
    }
  }

  getRouteForTemplate(template: Form): string | null { // Use the Form interface
    switch (template.form_id) {
      case 'new_patient_registration':
        return 'forms';
      default:
        return 'consent';
    }
  }

  showError(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 5000,
    });
  }

  getInitials(title: string): string {
    const words = title.split(' ');
    return words.length > 1
      ? (words[0][0] + words[1][0]).toUpperCase()
      : words[0][0].toUpperCase();
  }
}
